import { normalizePathSegmentList } from '@trimble/web3d-utils/path-segments';

const SURE_TILE_RE = /^s_[^/]+\.(json|glb)$/i;
export const TRIMBLE_HOST = 'fileservice.trimblecloud.com';

/**
 * Normalize tile paths for signed fileset member URLs. Preserves folder layout
 * from tileset JSON (entry/, L0/AP/, 3734_56279/entry_relative/, etc.).
 */
export function fixSureFlatPath(path: string): string {
  const raw = String(path ?? '').replace(/^\//, '');
  const segments = normalizePathSegmentList(
    raw.split('/').filter((segment) => segment.length > 0)
  );

  if (segments.length === 0) {
    return '';
  }

  const joined = segments.join('/');
  if (joined.includes('/')) {
    return joined;
  }

  const leaf = segments.at(-1) ?? joined;
  if (SURE_TILE_RE.test(leaf)) {
    return leaf;
  }

  return joined;
}

function tilesetBasePath(tilesetUrl: string): string {
  const tileset = new URL(tilesetUrl);
  return tileset.pathname.replace(/\/[^/]*$/, '/');
}

function filesetRootSegments(tilesetUrl: string): string[] {
  return tilesetBasePath(tilesetUrl)
    .split('/')
    .filter((segment) => segment.length > 0);
}

function sharesFilesetRoot(
  pathSegments: readonly string[],
  rootSegments: readonly string[]
): boolean {
  if (pathSegments.length < rootSegments.length) {
    return false;
  }

  return rootSegments.every(
    (segment, index) => pathSegments[index] === segment
  );
}

function tilePathFromUrl(requestUrl: string, tilesetUrl: string): string {
  const resolved = new URL(requestUrl, tilesetUrl);
  const rootSegments = filesetRootSegments(tilesetUrl);
  const pathSegments = resolved.pathname
    .split('/')
    .filter((segment) => segment.length > 0);

  const fallbackPath = (): string =>
    fixSureFlatPath(resolved.pathname.split('/').pop() ?? '');

  if (!sharesFilesetRoot(pathSegments, rootSegments)) {
    return fallbackPath();
  }

  const relative = normalizePathSegmentList(
    pathSegments.slice(rootSegments.length)
  );
  if (relative.length === 0) {
    return fallbackPath();
  }

  return fixSureFlatPath(relative.join('/'));
}

/** `new URL` throws TypeError for malformed input; anything else is a bug. */
function rethrowUnlessInvalidUrl(error: unknown): void {
  if (!(error instanceof TypeError)) {
    throw error;
  }
}

export function isRequestForTileset(
  requestUrl: string,
  tilesetUrl: string
): boolean {
  try {
    const tileset = new URL(tilesetUrl);
    const request = new URL(requestUrl, tilesetUrl);
    return (
      request.origin === tileset.origin &&
      request.pathname.startsWith(tilesetBasePath(tilesetUrl))
    );
  } catch (error: unknown) {
    rethrowUnlessInvalidUrl(error);
    return false;
  }
}

export function isTilesetJsonRequest(url: string, tilesetUrl: string): boolean {
  if (!url.includes(TRIMBLE_HOST) || !isRequestForTileset(url, tilesetUrl)) {
    return false;
  }

  try {
    return new URL(url).pathname.endsWith('.json');
  } catch (error: unknown) {
    rethrowUnlessInvalidUrl(error);
    return false;
  }
}

/**
 * Rewrite a Trimble FileService tile request to a signed flat-path URL.
 */
export function rewriteTrimbleTileUrl(
  requestUrl: string,
  tilesetUrl: string,
  templateUrl?: string
): string {
  if (!requestUrl.includes(TRIMBLE_HOST)) {
    return requestUrl;
  }

  if (!isRequestForTileset(requestUrl, tilesetUrl)) {
    return requestUrl;
  }

  try {
    const path = tilePathFromUrl(requestUrl, tilesetUrl);

    if (templateUrl?.includes('{path}')) {
      return templateUrl.replace('{path}', path);
    }

    const tileset = new URL(tilesetUrl);
    const target = new URL(tilesetUrl);
    target.pathname = tilesetBasePath(tilesetUrl) + path;
    target.search = tileset.search;
    return target.toString();
  } catch (error: unknown) {
    rethrowUnlessInvalidUrl(error);
    return requestUrl;
  }
}
